import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { 
  createProject, getProjects, updateProject, getProjectStatuses, assignEmployeesToProject, getProjectAssignments,
  createMilestone, getMilestones, deleteProject, assignEmployeesToMilestone, updateMilestone, deleteMilestone, getMilestoneAssignments,
  getProjectBudget, updateProjectBudget,
  exportProjects,
  createProjectStatus,
  reorderProjectStatuses,
  batchUpdateProjects, batchDeleteProjects, patchMilestone, createMilestoneJob, updateMilestoneJob, deleteMilestoneJob
} from "../../services/projectService";
import { getProjectLabels, setProjectLabels } from "../../services/labelService";
import { getEmployees } from "../../services/peopleService";
import { getAllClientCompanies, getClientContacts } from "../../services/leadService";
import ui from "./DashboardUI.module.css";
import FormModal from "../../components/forms/FormModal";
import DetailsModal from "../../components/forms/DetailsModal";
import detailStyles from "../../components/forms/DetailsModal.module.css";
import DataTable from "../../components/forms/DataTable";
import useTableColumns from "../../hooks/useTableColumns";
import Toast from "../../components/Toast";
import ColorPicker from "../../components/forms/ColorPicker";
import LabelSelector from "../../components/forms/LabelSelector";
import { useCurrentUser } from "../../context/UserContext";
import { useCurrency } from "../../hooks/useCurrency";
import { usePermissions } from "../../hooks/usePermission";
import ConfirmDialog from "../../components/ConfirmDialog";
import AddStatusModal from "../../components/AddStatusModal";
import StatusDropdown from "../../components/forms/StatusDropdown";
import CustomDropdown from "../../components/forms/CustomDropdown";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import ReorderListModal from "../../components/ReorderListModal";
import MilestoneEditModal from "./projects/MilestoneEditModal";
import plusIcon from "../../assets/icons/plus.svg";
import GanttView from "../../components/projects/GanttView";
import KanbanView from "../../components/projects/KanbanView";
import { getInitials, avatarColorFromName } from "../../utils/avatarUtils";
import kanban from "../../components/projects/KanbanView.module.css";
import EditableField from "../../components/common/EditableField";
import ActivityPanel from "../../components/common/ActivityPanel";
import Icon from "../../components/common/Icon";
import { FILE_UPLOADS_ENABLED, UPLOADS_DISABLED_MESSAGE } from "../../config/features";
import Avatar from "../../components/common/Avatar";
import * as activityService from "../../services/activityService";
import { getCustomFields } from "../../services/customFieldService";
import CustomFieldModal from "../../components/settings/CustomFieldModal";
import {
  columnKeyFor,
  isCustomColumnKey,
  fieldForColumnKey,
  getFieldValue,
  renderCustomValue,
  compareCustomValues,
} from "../../utils/customFieldUtils";
import CustomFieldRows from "../../components/common/CustomFieldRows";
import MilestoneRows, { MilestoneTag, DueDate, isMilestoneDone, isMilestoneLate, milestoneProgress } from "../../components/projects/MilestoneRows";
import useSelection from "../../hooks/useSelection";
import useBatchRunner from "../../hooks/useBatchRunner";
import BatchBar from "../../components/common/BatchBar";
import SegmentedControl from "../../components/common/SegmentedControl";
import RowActionsMenu from "../../components/common/RowActionsMenu";
import { exportPlainRows } from "../../utils/tableExport";
import ExportDialog, { sortAndFormatFields } from "../../components/common/ExportDialog";
import { cycleSort } from "../../utils/sortRows";
import JobListEditor from "../../components/projects/JobListEditor";

const COLUMN_STORAGE_KEY = "projectsColumns";

const BASE_PROJECT_COLUMNS = [
  { key: "client_company", label: "Company", visible: true },
  { key: "project_name", label: "Project", visible: true },
  { key: "client_name", label: "Client", visible: false },
  { key: "labels", label: "Labels", visible: true },
  { key: "email", label: "Email", visible: false },
  { key: "phone_number", label: "Phone", visible: false },
  { key: "start_date", label: "Start Date", visible: false },
  { key: "end_date", label: "End Date", visible: true },
  { key: "assignees", label: "Team", visible: true },
  { key: "urgency", label: "Urgency", visible: false },
  { key: "location", label: "Location", visible: false },
  { key: "address", label: "Address", visible: false },
  { key: "status", label: "Status", visible: true },
  { key: "progress", label: "Progress", visible: true },
];

// Project color palette - matches backend PROJECT_COLORS
const PROJECT_COLORS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#f97316', label: 'Orange' },
  { value: '#84cc16', label: 'Lime' },
  { value: '#6366f1', label: 'Indigo' },
  { value: '#14b8a6', label: 'Teal' },
];

const getRandomProjectColor = () => {
  const randomIndex = Math.floor(Math.random() * PROJECT_COLORS.length);
  return PROJECT_COLORS[randomIndex].value;
};

export default function Projects() {
  const currentUser = useCurrentUser();
  const { format: formatMoney, symbol: currencySymbol } = useCurrency();
  const { hasPermission } = usePermissions();
  const routeLocation = useLocation();

  // Permission checks
  const canViewAllProjects = hasPermission('view_projects');
  const canViewAssignedProjects = hasPermission('view_assigned_projects');
  const canCreateProjects = hasPermission('create_projects');
  const canEditProjects = hasPermission('edit_projects');
  const canDeleteProjects = hasPermission('delete_projects');
  const canManageProjectTeam = hasPermission('manage_project_team');
  const canViewProjectBudgets = hasPermission('view_project_budgets');
  const canManageProjectBudgets = hasPermission('manage_project_budgets');
  const canManageCompanySettings = hasPermission('manage_company_settings');

  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editableProject, setEditableProject] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);
  const [uploadingProjectFile, setUploadingProjectFile] = useState(false);
  const projectFileInputRef = useRef(null);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [teamSearchFocused, setTeamSearchFocused] = useState(false);
  const [editingLabels, setEditingLabels] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [showMilestoneEditModal, setShowMilestoneEditModal] = useState(false);
  const [editableMilestone, setEditableMilestone] = useState(null);
  const [activeMilestoneTab, setActiveMilestoneTab] = useState("Overview");
  const [milestoneEmployeeSearch, setMilestoneEmployeeSearch] = useState("");
  const [milestoneSearchFocused, setMilestoneSearchFocused] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [pendingArchiveAction, setPendingArchiveAction] = useState(null);
  const [showStatusReorderModal, setShowStatusReorderModal] = useState(false);
  const [savingStatusOrder, setSavingStatusOrder] = useState(false);

  // Form state
  const [clientCompany, setClientCompany] = useState("");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [urgency, setUrgency] = useState("Medium");
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(null);
  const [projectColor, setProjectColor] = useState(() => getRandomProjectColor());
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [projectLocation, setProjectLocation] = useState("");
  const [address, setAddress] = useState("");
  
  // Edit modal labels state
  const [editLabels, setEditLabels] = useState([]);
  
  // Budget state
  const [budgetCost, setBudgetCost] = useState("");
  const [budgetHours, setBudgetHours] = useState("");
  const [budgetNotes, setBudgetNotes] = useState("");

  // Export form state
  const [exportForm, setExportForm] = useState({
    startDate: "",
    endDate: "",
    statusId: "",
    urgency: "",
    includeMilestones: true,
    includeAssignments: true,
    includeBudget: canViewProjectBudgets,
    includeCustomFields: true,
    groupBy: "", // empty = no grouping, "company" = client company, "contact" = client name
    sortBy: "",
    sortDir: "asc",
    format: "xlsx",
  });

  // Client dropdown state
  const [allClientCompanies, setAllClientCompanies] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("overview");

  const [milestones, setMilestones] = useState([]);

  const [milestoneFormData, setMilestoneFormData] = useState({
    milestone_name: "",
    description: "",
    start_date: "",
    due_date: "",
    status: "open",
  });

  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Search, sort, filter, group state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterUrgency, setFilterUrgency] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLabel, setFilterLabel] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [groupBy, setGroupBy] = useState("status");
  const [showArchive, setShowArchive] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('projectsViewMode') || 'table';
    } catch {
      return 'table';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('projectsViewMode', viewMode); } catch {}
  }, [viewMode]);
  const [showAddStatusModal, setShowAddStatusModal] = useState(false);
  const [addStatusContext, setAddStatusContext] = useState(null); // 'table', 'create', 'edit'

  // Per-company custom fields applied to projects, defined in Settings or added
  // straight from the "+" at the end of the table header.
  const [customFields, setCustomFields] = useState([]);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);

  // Only ever follows a create from the table's "+" button, so the new field
  // has no values by definition and the loaded rows are still correct.
  // getFieldValue already treats a missing key as empty, so there is nothing to
  // patch into them — refetching the whole (unpaginated) list here would be the
  // most expensive request in the app fired for no new data.
  const refreshCustomFields = async () => {
    const data = await getCustomFields('project');
    setCustomFields(data.fields || []);
  };

  // Visibility and order remembered per user; custom field columns merge in once loaded.
  const { columns, toggleColumn, moveColumn } = useTableColumns(COLUMN_STORAGE_KEY, BASE_PROJECT_COLUMNS, customFields);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };


  // One cell renderer per custom field, merged into DataTable's static map.
  const customCellRenderers = useMemo(() => Object.fromEntries(
    customFields.map((field) => [
      columnKeyFor(field),
      (row) => renderCustomValue(field, getFieldValue(row, field), formatMoney) ?? "—",
    ])
  ), [customFields, formatMoney]);

  const toggleRowExpand = (projectId) => {
    setExpandedRows(prev => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // Reusable function to refresh all projects data
  const refreshProjects = async () => {
    try {
      const [statusesData, projectsData, archivedData] = await Promise.all([
        getProjectStatuses(),
        getProjects(),
        getProjects({ archived: true })
      ]);
      
      setStatuses(statusesData.statuses);
      
      const addStatusColor = (p) => {
        const status = statusesData.statuses.find(s => s.status_id === p.status_id);
        return { ...p, status_color: status?.color };
      };
      setProjects(projectsData.projects.map(addStatusColor));
      setArchivedProjects(archivedData.projects.map(addStatusColor));
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to refresh projects", "error");
    }
  };

  const applyStatusColors = (items, statusList) => {
    return items.map((project) => {
      const projectStatus = statusList.find((item) => item.status_id === project.status_id);
      return { ...project, status_color: projectStatus?.color };
    });
  };

  const handleSaveStatusOrder = async (orderedStatusIds) => {
    try {
      setSavingStatusOrder(true);
      const response = await reorderProjectStatuses(orderedStatusIds);
      const nextStatuses = response.statuses || [];

      setStatuses(nextStatuses);
      setProjects((prev) => applyStatusColors(prev, nextStatuses));
      setArchivedProjects((prev) => applyStatusColors(prev, nextStatuses));
      setShowStatusReorderModal(false);
      showToast('Project status order updated', 'success');
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed to reorder project statuses';
      showToast(msg, 'error');
    } finally {
      setSavingStatusOrder(false);
    }
  };

  // This effect intentionally runs against route/project state only to bootstrap modal opening once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
  const fetchData = async () => {
    try {
      const [statusesRes, projectsData, archivedData, employeesData, companiesData, customFieldsData] = await Promise.all([
        getProjectStatuses(),
        getProjects(),
        getProjects({ archived: true }),
        getEmployees(),
        getAllClientCompanies(),
        getCustomFields('project')
      ]);

      setStatuses(statusesRes.statuses);
      setCustomFields(customFieldsData.fields || []);

      const addStatusColor = (p) => {
        const status = statusesRes.statuses.find(s => s.status_id === p.status_id);
        return { ...p, status_color: status?.color };
      };
      setProjects(projectsData.projects.map(addStatusColor));
      setArchivedProjects(archivedData.projects.map(addStatusColor));

      setAvailableEmployees((employeesData.employees || employeesData || []).map(user => ({
        id: user.user_id,
        name: user.name,
        job_title: user.job_title
      })));

      setAllClientCompanies(companiesData);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to load projects", "error");
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);

  // Track if we've already handled the navigation state
  const [handledNavState, setHandledNavState] = useState(false);

  // Auto-open new project modal if navigated from dashboard
  useEffect(() => {
    // Skip if we've already handled the navigation state
    if (handledNavState) return;

    if (routeLocation.state?.openNewProjectModal) {
      setShowForm(true);
      setHandledNavState(true);
      // Clear the state so it doesn't reopen on subsequent renders
      window.history.replaceState({}, document.title);
    } else if (routeLocation.state?.openProjectDetailModal && projects.length > 0) {
      const projectId = routeLocation.state.openProjectDetailModal;
      const project = projects.find(p => p.project_id === projectId);
      if (project) {
        openProjectDetail(project);
        setHandledNavState(true);
        // Clear the state so it doesn't reopen on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation, projects, handledNavState]);

  useEffect(() => {
    if (statuses.length && status === null) {
      const defaultStatus = statuses.find(s => s.name === "In Queue") || statuses[0];
      setStatus(defaultStatus.status_id);
    }
  }, [statuses, status]);

  useEffect(() => {
    if (statuses.length) {
      const groupState = {};
      statuses.forEach((s) => {
        groupState[s.name] = false;
      });
      setCollapsedGroups(groupState);
    }
  }, [statuses]);

  const toggleGroupCollapse = (status) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const handleSort = (columnKey) => {
    const next = cycleSort(sortColumn ? { key: sortColumn, dir: sortDirection } : null, columnKey);
    setSortColumn(next?.key ?? null);
    setSortDirection(next?.dir ?? "asc");
  };

  // Compute filtered and sorted project list
  const getDisplayedProjects = () => {
    let result = viewMode === 'kanban' ? [...projects, ...archivedProjects] : showArchive ? [...archivedProjects] : [...projects];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          (p.project_name || "").toLowerCase().includes(term) ||
          (p.client_company || "").toLowerCase().includes(term) ||
          (p.client_name || "").toLowerCase().includes(term)
      );
    }

    // Urgency filter
    if (filterUrgency) {
      result = result.filter((p) => p.urgency === filterUrgency);
    }

    // Status filter
    if (filterStatus) {
      result = result.filter((p) => String(p.status_id) === filterStatus);
    }

    // Label filter
    if (filterLabel) {
      result = result.filter((p) =>
        p.labels && p.labels.some((l) => String(l.label_id) === filterLabel)
      );
    }

    // Employee filter
    if (filterEmployee) {
      result = result.filter((p) =>
        p.assignees && p.assignees.some((a) => String(a.employee_id) === filterEmployee)
      );
    }

    // Sorting
    if (sortColumn) {
      // Custom fields carry their own type, so they sort by it rather than
      // falling through to the string comparison below.
      const customField = isCustomColumnKey(sortColumn)
        ? fieldForColumnKey(customFields, sortColumn)
        : null;

      result = [...result].sort((a, b) => {
        if (customField) {
          const comparison = compareCustomValues(
            customField,
            getFieldValue(a, customField),
            getFieldValue(b, customField)
          );
          return sortDirection === "asc" ? comparison : -comparison;
        }

        let valA = a[sortColumn];
        let valB = b[sortColumn];

        // Handle date columns
        if (sortColumn === "start_date" || sortColumn === "end_date") {
          valA = valA ? new Date(valA).getTime() : 0;
          valB = valB ? new Date(valB).getTime() : 0;
        } else if (sortColumn === "assignees") {
          valA = a.assignee_count || 0;
          valB = b.assignee_count || 0;
        } else if (sortColumn === "progress") {
          valA = milestoneProgress(a);
          valB = milestoneProgress(b);
        } else if (sortColumn === "urgency") {
          const order = { High: 3, Medium: 2, Low: 1 };
          valA = order[valA] || 0;
          valB = order[valB] || 0;
        } else if (sortColumn === "status") {
          const statusA = statuses.find((s) => s.status_id === a.status_id);
          const statusB = statuses.find((s) => s.status_id === b.status_id);
          valA = (statusA?.name || "").toLowerCase();
          valB = (statusB?.name || "").toLowerCase();
        } else {
          valA = (valA || "").toString().toLowerCase();
          valB = (valB || "").toString().toLowerCase();
        }

        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  };

  const displayedProjects = getDisplayedProjects();

  const archivedCount = archivedProjects.length;

  const availableLabels = useMemo(() => {
    const labelMap = new Map();
    const source = showArchive ? archivedProjects : projects;
    source.forEach((p) => {
      (p.labels || []).forEach((l) => {
        if (!labelMap.has(l.label_id)) labelMap.set(l.label_id, l.name);
      });
    });
    return Array.from(labelMap.entries());
  }, [projects, archivedProjects, showArchive]);

  const hasActiveFilters = !!(searchTerm || filterUrgency || filterStatus || filterLabel || filterEmployee);

  const resetView = () => {
    setSearchTerm("");
    setFilterUrgency("");
    setFilterStatus("");
    setFilterLabel("");
    setFilterEmployee("");
    setSortColumn(null);
    setSortDirection("asc");
    setGroupBy("status");
  };

  const getGroupByValue = () => {
    if (!groupBy) return null;
    if (groupBy === "status") {
      return (project) => {
        const match = statuses.find(s => s.status_id === project.status_id);
        return match?.name?.trim() || "Unknown";
      };
    }
    return groupBy;
  };

  const handleMilestoneAssignmentSave = async () => {
    if (!editableMilestone?.milestone_id) {
      showToast("Invalid milestone", "error");
      return;
    }

    // Validate dates: start date should be same or earlier than due date
    if (editableMilestone.start_date && editableMilestone.due_date) {
      const startDate = new Date(editableMilestone.start_date);
      const dueDate = new Date(editableMilestone.due_date);
      if (startDate > dueDate) {
        showToast("Start date cannot be later than due date", "error");
        return;
      }
    }

    try {
      await updateMilestone(editableMilestone.milestone_id, {
        milestone_name: editableMilestone.milestone_name,
        description: editableMilestone.description,
        start_date: editableMilestone.start_date || null,
        due_date: editableMilestone.due_date || null,
        status: editableMilestone.status,
      });

      const teamPayload = {
        assignedBy: currentUser.user_id,
        employees: (editableMilestone.team_members || []).map(emp => ({
          employee_id: emp.id,
          role: emp.role || "",
        }))
      };

      await assignEmployeesToMilestone(editableMilestone.milestone_id, teamPayload);

      // Refresh all projects to get updated milestone data
      await refreshProjects();

      // Also update local milestones state for the modal
      if (editableProject) {
        const updatedMilestones = await getMilestones(editableProject.project_id);
        setMilestones(Array.isArray(updatedMilestones) ? updatedMilestones : []);
      }

      setShowMilestoneEditModal(false);
      showToast("Milestone saved successfully", "success");
    } catch (err) {
      console.error("Failed to save milestone:", err);
      showToast(err.message || "Failed to save milestone", "error");
    }
  };

  // Files tab upload. The POST response has no uploaded_by_name (that column
  // comes from a JOIN in the list query), so refetch rather than prepending the
  // response — otherwise the new row reads "Unknown" until the modal reopens.
  const handleProjectFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editableProject?.project_id) return;

    setUploadingProjectFile(true);
    try {
      await activityService.uploadFile('project', editableProject.project_id, file);
      const files = await activityService.listFiles('project', editableProject.project_id);
      setProjectFiles(files || []);
      setActivityRefreshKey((k) => k + 1);
      showToast("File uploaded", "success");
    } catch (err) {
      console.error("Failed to upload file:", err);
      showToast(err.message || "Failed to upload file", "error");
    } finally {
      setUploadingProjectFile(false);
    }
  };

  const handleProjectFileDelete = async (file) => {
    try {
      await activityService.deleteFile('project', editableProject.project_id, file.file_id);
      setProjectFiles((prev) => prev.filter((f) => f.file_id !== file.file_id));
      setActivityRefreshKey((k) => k + 1);
    } catch (err) {
      showToast(err.message || "Failed to delete file", "error");
    }
  };

  // Deleting a milestone cascades to employee_bookings, so the confirm has to
  // name that consequence rather than just asking "are you sure?".
  const handleDeleteMilestone = async (milestone) => {
    const confirmed = window.confirm(
      `Delete milestone "${milestone.milestone_name}"?\n\n` +
      `Any scheduled bookings for this milestone will be removed as well. ` +
      `Logged timesheet hours are kept, but will no longer be linked to it.`
    );
    if (!confirmed) return;

    try {
      const result = await deleteMilestone(milestone.milestone_id);
      setMilestones((prev) => prev.filter((m) => m.milestone_id !== milestone.milestone_id));
      await refreshProjects();

      const removed = result?.removedBookings || 0;
      showToast(
        removed > 0
          ? `Milestone deleted — ${removed} scheduled booking${removed === 1 ? "" : "s"} also removed`
          : "Milestone deleted",
        "success"
      );
    } catch (err) {
      console.error("Failed to delete milestone:", err);
      showToast(err.message || "Failed to delete milestone", "error");
    }
  };

  const handleEditMilestone = async (milestone) => {
    try {
      const team = await getMilestoneAssignments(milestone.milestone_id);
      const teamMembers = team.map((a) => ({
        id: a.employee_id,
        name: a.user_name,
        job_title: a.job_title,
        role: a.role_name,
        role_id: a.role_id,
      }));

      setEditableMilestone({
        ...milestone,
        team_members: teamMembers,
      });

      setShowMilestoneEditModal(true);
    } catch (err) {
      console.error("Failed to load milestone team:", err);
      showToast(err.message || "Failed to load milestone team", "error");

      setEditableMilestone({
        ...milestone,
        team_members: [],
      });

      setShowMilestoneEditModal(true);
    }
  };


  const clearForm = () => {
    setProjectName("");
    setClientName("");
    setClientCompany("");
    setSelectedLabels([]);
    setEmail("");
    setPhoneNumber("");
    setAssignedEmployees([]);
    setStartDate("");
    setEndDate("");
    setUrgency("Medium");
    setDescription("");
    setStatus(statuses.find(s => s.name === "In Queue")?.status_id || statuses[0]?.status_id || null);
    setProjectColor(getRandomProjectColor());
    setBudgetCost("");
    setBudgetHours("");
    setBudgetNotes("");
    setProjectLocation("");
    setAddress("");
    setActiveTab("overview");
    setError(null);
    // Clear dropdown state
    setCompanySuggestions([]);
    setNameSuggestions([]);
    setClientContacts([]);
    setShowCompanyDropdown(false);
    setShowNameDropdown(false);
  };

  const handleCompanyFocus = () => {
    setShowCompanyDropdown(true);
    setCompanySuggestions(allClientCompanies.map(c => c.company_name));
  };

  const handleCompanyInput = (val) => {
    setClientCompany(val);
    
    if (val.trim() === '') {
      setCompanySuggestions(allClientCompanies.map(c => c.company_name));
    } else {
      // Check if there's an exact match
      const exactMatch = allClientCompanies.find(c => 
        c.company_name.toLowerCase() === val.toLowerCase()
      );
      
      if (exactMatch) {
        // If exact match exists, only show that
        setCompanySuggestions([exactMatch.company_name]);
      } else {
        // Otherwise, show all that start with the query
        const filtered = allClientCompanies
          .filter(c => c.company_name.toLowerCase().startsWith(val.toLowerCase()))
          .map(c => c.company_name);
        setCompanySuggestions(filtered);
      }
    }
  };

  const handleCompanySelect = (companyName) => {
    setClientCompany(companyName);
    setCompanySuggestions([]);
    setShowCompanyDropdown(false);
    // When company is selected, load contacts for that company
    loadContactsForCompany(companyName);
  };

  const loadContactsForCompany = async (companyName) => {
    if (!companyName) {
      setClientContacts([]);
      return;
    }
    const company = allClientCompanies.find(c => c.company_name === companyName);
    if (company) {
      const contacts = await getClientContacts(company.client_company_id);
      setClientContacts(contacts);
    }
  };

  const handleNameFocus = async () => {
    setShowNameDropdown(true);
    if (clientCompany) {
      // Already have contacts loaded for this company
      updateNameSuggestions(clientName);
    } else {
      // Load all contacts when no company is selected
      if (!clientContacts.length) {
        const allContacts = await getClientContacts(null);
        setClientContacts(allContacts);
      }
      updateNameSuggestions(clientName);
    }
  };

  const handleNameInput = (val) => {
    setClientName(val);
    updateNameSuggestions(val);
  };

  const updateNameSuggestions = (query) => {
    let suggestions = [];
    if (clientCompany && clientContacts.length > 0) {
      // Filter contacts for selected company
      suggestions = clientContacts
        .filter(contact => contact.name && contact.name.trim())
        .filter(contact => 
          !query || contact.name.toLowerCase().startsWith(query.toLowerCase())
        )
        .map(contact => contact.name);
    } else if (!clientCompany && clientContacts.length > 0) {
      // No company selected, show all contacts
      suggestions = clientContacts
        .filter(contact => contact.name && contact.name.trim())
        .filter(contact => 
          !query || contact.name.toLowerCase().startsWith(query.toLowerCase())
        )
        .map(contact => contact.name);
    }
    setNameSuggestions(suggestions);
  };

  const handleNameSelect = (contactName) => {
    setClientName(contactName);
    setNameSuggestions([]);
    setShowNameDropdown(false);
    
    // Autofill email and phone if contact exists
    const contact = clientContacts.find(c => c.name === contactName);
    if (contact) {
      setEmail(contact.email || '');
      setPhoneNumber(contact.phone_number || '');
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate dates: start date should be same or earlier than end date
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        showToast("Start date cannot be later than end date", "error");
        return;
      }
    }

    try {
      const result = await createProject({
        client_name: clientName,
        client_company: clientCompany,
        project_name: projectName,
        email,
        phone_number: phoneNumber,
        start_date: startDate,
        end_date: endDate,
        urgency,
        description,
        status_id: status,
        color: projectColor,
        budget_cost: budgetCost ? parseFloat(budgetCost) : null,
        budget_hours: budgetHours ? parseFloat(budgetHours) : null,
        location: projectLocation,
        address,
      });

      // Save labels for the new project
      if (result.project && selectedLabels.length > 0) {
        await setProjectLabels(
          result.project.project_id,
          selectedLabels.map(l => l.label_id)
        );
      }

      setShowForm(false);
      clearForm();
      setError(null);

      await refreshProjects();
      showToast("Project created successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save project", "error");
    }
  };

  const handleExportSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build query params for export
      const params = {};

      if (exportForm.startDate) params.start_date = exportForm.startDate;
      if (exportForm.endDate) params.end_date = exportForm.endDate;
      if (exportForm.statusId) params.status_id = exportForm.statusId;
      if (exportForm.urgency) params.urgency = exportForm.urgency;
      if (exportForm.includeMilestones) params.include_milestones = 'true';
      if (exportForm.includeAssignments) params.include_assignments = 'true';
      if (exportForm.includeBudget && canViewProjectBudgets) params.include_budget = 'true';
      if (exportForm.includeCustomFields) params.include_custom_fields = 'true';
      if (exportForm.groupBy) params.group_by = exportForm.groupBy;
      if (exportForm.sortBy) Object.assign(params, { sort_by: exportForm.sortBy, sort_dir: exportForm.sortDir });

      const data = await exportProjects(params);
      await exportPlainRows({ ...data, filename: `projects_export_${new Date().toISOString().split('T')[0]}`, format: exportForm.format });
      showToast('Projects exported successfully', 'success');
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      showToast(error.message || "Failed to export projects", "error");
    }
  };

  // Inline status edit: changing to "Completed" archives the project, so
  // route through the same confirm dialog the rest of the app uses before
  // actually persisting the change.
  // "12 Mar 2026" — unambiguous without being as long as a locale date string.
  const formatDetailDate = (isoDate) => {
    if (!isoDate) return null;
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return isoDate;
    return parsed.toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const renderMilestonePill = (status) => {
    const raw = (status || 'open').replace(/_/g, ' ');
    const label = raw.charAt(0).toUpperCase() + raw.slice(1);
    const tone = status === 'completed'
      ? { background: '#f0fdf4', color: '#15803d', dot: '#22c55e' }
      : status === 'in_progress'
        ? { background: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6' }
        : { background: '#f1f5f9', color: '#475569', dot: '#94a3b8' };
    return (
      <span className={detailStyles.pill} style={{ background: tone.background, color: tone.color }}>
        <span className={detailStyles.pillDot} style={{ background: tone.dot }} />
        {label}
      </span>
    );
  };

  // Status and urgency read as the same pill wherever they appear — in the
  // title band and in the Overview property list — so neither spot looks like
  // the "real" one.
  const renderStatusPill = (statusId) => {
    const status = statuses.find(s => s.status_id === Number(statusId));
    if (!status) return null;
    const color = status.color || '#94a3b8';
    return (
      <span
        className={detailStyles.pill}
        // Tinted with the status' own colour (hex + alpha) rather than a flat
        // grey, so the pill carries the same meaning it does in the board view.
        style={{ background: `${color}1f`, color: '#334155' }}
      >
        <span className={detailStyles.pillDot} style={{ background: color }} />
        {status.name}
      </span>
    );
  };

  const renderUrgencyPill = (urgency) => {
    const level = urgency || 'Low';
    const tone = level === 'High'
      ? { background: '#fef2f2', color: '#b91c1c', dot: '#dc2626' }
      : level === 'Medium'
        ? { background: '#fffbeb', color: '#b45309', dot: '#f59e0b' }
        : { background: '#f0fdf4', color: '#15803d', dot: '#22c55e' };
    return (
      <span className={detailStyles.pill} style={{ background: tone.background, color: tone.color }}>
        <span className={detailStyles.pillDot} style={{ background: tone.dot }} />
        {level}
      </span>
    );
  };

  const handleInlineProjectStatusChange = async (newStatusIdStr) => {
    const newStatusId = Number(newStatusIdStr);
    const newStatus = statuses.find(s => s.status_id === newStatusId);
    const originalStatus = statuses.find(s => s.status_id === editableProject.status_id);
    const isChangingToCompleted = newStatus?.name?.toLowerCase() === 'completed'
      && originalStatus?.name?.toLowerCase() !== 'completed';

    const applyStatusChange = async () => {
      await updateProjectAndRefresh(editableProject.project_id, { status_id: newStatusId });
      setEditableProject(prev => ({ ...prev, status_id: newStatusId }));
      applyOptimisticProjectUpdate(editableProject.project_id, { status_id: newStatusId });
    };

    if (isChangingToCompleted) {
      setPendingArchiveAction(() => applyStatusChange);
      setShowArchiveConfirm(true);
      return;
    }

    await applyStatusChange();
  };

  const persistTeamAssignments = async (updatedList) => {
    try {
      await assignEmployeesToProject(editableProject.project_id, {
        projectId: editableProject.project_id,
        clientId: editableProject.client_id || null,
        assignedBy: currentUser.user_id,
        employees: updatedList.map((emp) => ({
          user_id: emp.id,
          employee_id: emp.id,
          role: emp.role,
        })),
      });
      setAssignedEmployees(updatedList);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update team", "error");
    }
  };

  const handleAddTeamMember = (emp) => {
    setEmployeeSearch("");
    persistTeamAssignments([...assignedEmployees, { ...emp, role: "" }]);
  };

  const handleRemoveTeamMember = (empId) => {
    persistTeamAssignments(assignedEmployees.filter((e) => e.id !== empId));
  };

  const applyOptimisticProjectUpdate = (projectId, patch) => {
    let previous = null;
    setProjects((prev) => prev.map((p) => {
      if (p.project_id !== projectId) return p;
      previous = p;
      const next = { ...p, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'status_id')) {
        const matching = statuses.find(s => s.status_id === patch.status_id);
        if (matching) {
          next.status_color = matching.color;
          next.status_name = matching.name;
        }
      }
      return next;
    }));
    return previous;
  };

  const rollbackProject = (previous) => {
    if (!previous) return;
    setProjects((prev) => prev.map((p) => p.project_id === previous.project_id ? previous : p));
  };

  const handleStatusChange = async (project, newStatusId) => {
    const newStatus = statuses.find(s => s.status_id === newStatusId);
    if (newStatus && newStatus.name?.toLowerCase() === 'completed') {
      setPendingArchiveAction(() => async () => {
        const previous = applyOptimisticProjectUpdate(project.project_id, { status_id: newStatusId });
        try {
          await updateProject(project.project_id, { status_id: newStatusId });
          await refreshProjects();
          showToast("Status updated successfully", "success");
        } catch (err) {
          console.error(err);
          rollbackProject(previous);
          showToast(err.message || "Failed to update status", "error");
        }
      });
      setShowArchiveConfirm(true);
      return;
    }
    const previous = applyOptimisticProjectUpdate(project.project_id, { status_id: newStatusId });
    try {
      await updateProject(project.project_id, { status_id: newStatusId });
      await refreshProjects();
      showToast("Status updated successfully", "success");
    } catch (err) {
      console.error(err);
      rollbackProject(previous);
      showToast(err.message || "Failed to update status", "error");
    }
  };

  const handleCreateStatus = async ({ name, color, description }) => {
    const result = await createProjectStatus({ name, color, description });
    const newStatus = result.status;
    // Refresh statuses list
    const statusesRes = await getProjectStatuses();
    setStatuses(statusesRes.statuses);
    // Auto-select the new status in the relevant context
    if (addStatusContext === 'create') {
      setStatus(newStatus.status_id);
    } else if (addStatusContext === 'edit' && editableProject) {
      setEditableProject({ ...editableProject, status_id: newStatus.status_id });
    }
    setShowAddStatusModal(false);
    setAddStatusContext(null);
    showToast(`Status "${newStatus.name}" created`, "success");
  };

  const handleRestoreProject = async (project) => {
    try {
      const firstStatus = statuses.find(s => s.name?.toLowerCase() !== "completed");
      if (!firstStatus) {
        showToast("No active status available to restore to", "error");
        return;
      }
      await updateProject(project.project_id, { status_id: firstStatus.status_id });
      await refreshProjects();
      showToast("Project restored successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to restore project", "error");
    }
  };

  const handleUrgencyChange = async (project, newUrgency) => {
    const previous = applyOptimisticProjectUpdate(project.project_id, { urgency: newUrgency });
    try {
      await updateProject(project.project_id, { urgency: newUrgency });
      await refreshProjects();
      showToast("Urgency updated successfully", "success");
    } catch (err) {
      console.error(err);
      rollbackProject(previous);
      showToast(err.message || "Failed to update urgency", "error");
    }
  };

  // ─── Batch selection (table view) ───────────────────────────────────
  const { selectedIds, toggleRow: toggleSelectRow, toggleGroup: toggleSelectGroup, toggleAll, clear: clearSelection } = useSelection();
  const runBatch = useBatchRunner(setToast);
  const [confirmBatchDelete, setConfirmBatchDelete] = useState(false);

  // Applies one change to every ticked project; Undo restores each project's previous value.
  const batchChange = (patch) => {
    const ids = Array.from(selectedIds);
    const picked = displayedProjects.filter((p) => selectedIds.has(p.project_id));
    const regroup = (key) => {
      const byValue = new Map();
      picked.forEach((p) => byValue.set(p[key], [...(byValue.get(p[key]) || []), p.project_id]));
      return Array.from(byValue, ([value, group]) => ({ ids: group, patch: { [key]: value } }));
    };
    // The server says which projects an add actually changed, so Undo never removes someone who was already there.
    const undoFor = (result) => {
      if (patch.status_id) return regroup("status_id");
      if (patch.urgency) return regroup("urgency");
      if (patch.add_employee_id) return [{ ids: result.added_team_project_ids, patch: { remove_employee_id: patch.add_employee_id } }];
      return [{ ids: result.added_label_project_ids, patch: { remove_label_id: patch.add_label_id } }];
    };
    runBatch(
      () => batchUpdateProjects(ids, patch),
      refreshProjects,
      () => `${ids.length} project${ids.length === 1 ? "" : "s"} updated`,
      (result) => Promise.all(undoFor(result).filter((u) => u.ids.length).map((u) => batchUpdateProjects(u.ids, u.patch)))
    );
  };

  const batchDelete = () => {
    const ids = Array.from(selectedIds);
    setConfirmBatchDelete(false);
    runBatch(
      () => batchDeleteProjects(ids),
      refreshProjects,
      () => `${ids.length} project${ids.length === 1 ? "" : "s"} deleted`
    ).then((ok) => ok && clearSelection());
  };

  // Moving projects to Completed archives them, so ask first like the single-project status change does.
  const batchStatus = (statusId) => {
    const apply = () => batchChange({ status_id: statusId });
    if (statuses.find((s) => s.status_id === statusId)?.name?.toLowerCase() !== "completed") return apply();
    setPendingArchiveAction(() => apply);
    setShowArchiveConfirm(true);
  };

  const [showBatchExport, setShowBatchExport] = useState(false);
  const statusName = (p) => statuses.find((s) => s.status_id === p.status_id)?.name;
  const projectExportColumns = [
    { key: 'client_company', label: 'Company', value: (p) => p.client_company, visible: true },
    { key: 'project_name', label: 'Project', value: (p) => p.project_name, visible: true },
    { key: 'status', label: 'Status', value: statusName, visible: true },
    { key: 'urgency', label: 'Urgency', value: (p) => p.urgency, visible: true },
    { key: 'start_date', label: 'Start', value: (p) => p.start_date, visible: true },
    { key: 'end_date', label: 'End', value: (p) => p.end_date, visible: true },
    { key: 'assignees', label: 'Team', value: (p) => (p.assignees || []).map((a) => a.user_name).join(", "), visible: true },
    { key: 'labels', label: 'Labels', value: (p) => (p.labels || []).map((l) => l.name).join(", "), visible: true },
    { key: 'milestones', label: 'Milestones done', value: (p) => `${(p.milestones || []).filter(isMilestoneDone).length}/${(p.milestones || []).length}`, visible: true },
    { key: 'client_name', label: 'Client', value: (p) => p.client_name, visible: false },
    { key: 'location', label: 'Location', value: (p) => p.location, visible: false },
    { key: 'address', label: 'Address', value: (p) => p.address, visible: false },
    { key: 'description', label: 'Description', value: (p) => p.description, visible: false },
  ];

  // ─── Milestones and jobs in the table ────────────────────────────────
  const patchMilestoneLocal = (milestoneId, change) => {
    const apply = (list) => list.map((p) => (p.milestones?.some((m) => m.milestone_id === milestoneId)
      ? { ...p, milestones: p.milestones.map((m) => (m.milestone_id === milestoneId ? { ...m, ...change(m) } : m)) }
      : p));
    setProjects(apply);
    setArchivedProjects(apply);
    setMilestones((prev) => prev.map((m) => (m.milestone_id === milestoneId ? { ...m, ...change(m) } : m)));
  };

  const handleToggleMilestone = async (m) => {
    const status = isMilestoneDone(m) ? "open" : "completed";
    try {
      await patchMilestone(m.milestone_id, status);
      patchMilestoneLocal(m.milestone_id, () => ({ status }));
    } catch (err) {
      showToast(err.response?.data?.error || err.message || "Failed to update milestone", "error");
    }
  };

  const [openJobsFor, setOpenJobsFor] = useState(null);

  const applyJobChange = async (m, change, request) => {
    try {
      const { job, milestone } = await request();
      patchMilestoneLocal(m.milestone_id, (cur) => ({ status: milestone.status, jobs: change(cur.jobs || [], job) }));
      setActivityRefreshKey((k) => k + 1);
    } catch (err) {
      showToast(err.response?.data?.error || err.message || "Failed to update jobs", "error");
    }
  };
  const replaceJob = (job) => (jobs, updated) => jobs.map((j) => (j.id === job.id ? updated : j));
  const handleToggleJob = (job, m) => applyJobChange(m, replaceJob(job), () => updateMilestoneJob(job.id, { done: !job.done_at }));
  const handleAddJob = (m, title) => applyJobChange(m, (jobs, job) => [...jobs, job], () => createMilestoneJob(m.milestone_id, title));
  const handleRenameJob = (m, job, title) => applyJobChange(m, replaceJob(job), () => updateMilestoneJob(job.id, { title }));
  const handleRemoveJob = (m, job) => applyJobChange(m, (jobs) => jobs.filter((j) => j.id !== job.id), () => deleteMilestoneJob(job.id));

  // The project's team can tick jobs without edit rights; projects shown to assigned-only viewers are all theirs.
  const canTickJobs = (project) => canEditProjects
    || (!canViewAllProjects && canViewAssignedProjects)
    || (project.assignees || []).some((a) => a.employee_id === currentUser?.employee_id);

  // Wrap the two update calls used by the detail modal's inline-edit
  // fields so every field save also refreshes the open ActivityPanel,
  // which has no other way to know a field changed elsewhere in the modal.
  const updateProjectAndRefresh = async (id, fields) => {
    const result = await updateProject(id, fields);
    setActivityRefreshKey(k => k + 1);
    return result;
  };

  const updateProjectBudgetAndRefresh = async (id, fields) => {
    const result = await updateProjectBudget(id, fields);
    setActivityRefreshKey(k => k + 1);
    return result;
  };

  const openProjectDetail = async (project) => {
    try {
      setEditableProject(project);
      
      // Load contacts for the existing company
      if (project.client_company) {
        await loadContactsForCompany(project.client_company);
      }
      
      const assignments = await getProjectAssignments(project.project_id);
      const employeeList = Array.isArray(assignments) ? assignments : (assignments.assignments || []);
      
      const mappedEmployees = employeeList.map(a => 
      ({
        id: a.employee_id,
        name: a.user_name,
        role: a.role_name,
        job_title: a.job_title,
      }));
      
      setAssignedEmployees(mappedEmployees);

      // Fetch labels for this project
      try {
        const labelData = await getProjectLabels(project.project_id);
        setEditLabels(labelData.labels || []);
      } catch (err) {
        console.error("Failed to fetch project labels:", err);
        setEditLabels([]);
      }

      const milestonesData = await getMilestones(project.project_id);
      setMilestones(Array.isArray(milestonesData) ? milestonesData : []);

      try {
        const files = await activityService.listFiles('project', project.project_id);
        setProjectFiles(files || []);
      } catch (err) {
        console.error("Failed to fetch project files:", err);
        setProjectFiles([]);
      }

      if (canViewProjectBudgets) {
        try {
          const budgetData = await getProjectBudget(project.project_id);
          setBudgetCost(budgetData.budget_cost || "");
          setBudgetHours(budgetData.budget_hours || "");
          setBudgetNotes(budgetData.notes || "");
        } catch (err) {
          console.warn("Could not fetch budget (user may not have permission):", err);
          setBudgetCost("");
          setBudgetHours("");
          setBudgetNotes("");
        }
      } else {
        setBudgetCost("");
        setBudgetHours("");
        setBudgetNotes("");
      }

      setShowDetailModal(true);
    } catch (err) {
      console.error("Failed to load project details:", err);
      showToast(err.message || "Failed to load project details", "error");
    }
  };

  const overviewFields = [
    { 
      section: "overview", 
      label: "Project Name", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            style={{ flex: 1 }}
          />
          <ColorPicker
            value={projectColor}
            onChange={(e) => setProjectColor(e.target.value)}
          />
        </div>
      )
    },
    { 
      section: "overview", 
      label: "Client Details", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Client Name</label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => handleNameInput(e.target.value)}
              onFocus={handleNameFocus}
              onBlur={() => setTimeout(() => setShowNameDropdown(false), 200)}
              style={{ width: '100%' }}
            />
            {nameSuggestions.length > 0 && showNameDropdown && (
              <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                {nameSuggestions.map((s, i) => (
                  <li key={i} onClick={() => handleNameSelect(s)} style={{ padding: '8px 12px', cursor: 'pointer' }}>{s}</li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Company Name</label>
            <input
              type="text"
              value={clientCompany}
              onChange={(e) => handleCompanyInput(e.target.value)}
              onFocus={handleCompanyFocus}
              onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
              style={{ width: '100%' }}
            />
            {companySuggestions.length > 0 && showCompanyDropdown && (
              <ul style={{ position: 'absolute', zIndex: 10, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', listStyle: 'none', padding: '4px 0', margin: '4px 0 0', width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                {companySuggestions.map((s, i) => (
                  <li key={i} onClick={() => handleCompanySelect(s)} style={{ padding: '8px 12px', cursor: 'pointer' }}>{s}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )
    },
    { section: "overview", label: "Email", value: email, onChange: (e) => setEmail(e.target.value), type: "email" },
    { section: "overview", label: "Phone Number", value: phoneNumber, onChange: (e) => setPhoneNumber(e.target.value), type: "tel" },
    { 
      section: "overview", 
      label: "Labels", 
      type: "custom",
      render: () => (
        <LabelSelector
          selectedLabels={selectedLabels}
          onChange={setSelectedLabels}
          placeholder="Add service type labels..."
        />
      )
    },
    { 
      section: "overview", 
      label: "Project Dates", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      )
    },
    { 
      section: "overview", 
      label: "Project Status", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Urgency</label>
            <CustomDropdown
              options={[
                { value: 'Low', label: 'Low' },
                { value: 'Medium', label: 'Medium' },
                { value: 'High', label: 'High' },
              ]}
              value={urgency}
              onChange={(val) => setUrgency(val)}
              fullWidth
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Status</label>
            <StatusDropdown
              statuses={statuses}
              value={status || null}
              onChange={(id) => setStatus(id)}
              onAddNew={() => { setAddStatusContext('create'); setShowAddStatusModal(true); }}
              fullWidth
            />
          </div>
        </div>
      )
    },
    { 
      section: "overview", 
      label: "Location & Address", 
      type: "custom",
      render: () => (
        <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Location</label>
            <input
              type="text"
              value={projectLocation}
              onChange={(e) => setProjectLocation(e.target.value)}
              placeholder="e.g., New York, NY"
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Address</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Full street address"
              style={{ width: '100%', minHeight: '60px' }}
            />
          </div>
        </div>
      )
    },
    { section: "overview", label: "Description", value: description, onChange: (e) => setDescription(e.target.value), type: "textarea" },
  ];

  const budgetFields = [
    {
      label: `Estimated Budget (${currencySymbol})`,
      value: budgetCost,
      onChange: (e) => setBudgetCost(e.target.value),
      type: "number",
      step: "0.01",
      min: "0",
      placeholder: "0.00"
    },
    {
      label: "Estimated Hours",
      value: budgetHours,
      onChange: (e) => setBudgetHours(e.target.value),
      type: "number",
      step: "0.5",
      min: "0",
      placeholder: "0"
    },
    {
      label: "Budget Notes",
      value: budgetNotes,
      onChange: (e) => setBudgetNotes(e.target.value),
      type: "textarea",
      placeholder: "Additional notes about the budget..."
    }
  ];

  // Show access denied if no view permissions
  if (!canViewAllProjects && !canViewAssignedProjects) {
    return (
      <div className={ui.emptyState}>
        <p>You do not have permission to view projects.</p>
      </div>
    );
  }

  const isViewingAssignedOnly = !canViewAllProjects && canViewAssignedProjects;

  return (
    <div className={`${ui.pageContainer} ${(viewMode === 'gantt' || viewMode === 'kanban') ? ui.pageContainerGantt : ''}`}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          action={toast.action}
          onClose={() => setToast(null)}
        />
      )}

      {/* Page Header */}
      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft}>
          <h1 className={ui.pageTitle}>Projects</h1>
          {!showLoading && statuses.length > 0 && (
            <span className={ui.pageCount}>
              {projects.length} active · {archivedCount} completed
            </span>
          )}
        </div>
        <div className={ui.pageHeaderRight}>
          <SegmentedControl
            ariaLabel="View mode"
            options={[{ value: 'table', label: 'Table' }, { value: 'kanban', label: 'Board' }, { value: 'gantt', label: 'Timeline' }]}
            value={viewMode}
            onChange={(mode) => { setViewMode(mode); if (mode !== 'table') setShowArchive(false); }}
          />

          <RowActionsMenu
            actions={[
              { label: "Export", onClick: () => setShowExportModal(true) },
              ...(canManageCompanySettings && statuses.length > 1
                ? [{ label: "Manage statuses", onClick: () => setShowStatusReorderModal(true) }]
                : []),
            ]}
          />

          {canCreateProjects && (
            <button className={ui.addButton} onClick={() => setShowForm(true)}>
              <img src={plusIcon} alt="" width="16" height="16" />
              Add Project
            </button>
          )}
        </div>
      </div>

      {/* Toolbar: Search, Filter, Group */}
      {!showLoading && (
        <div className={ui.tableToolbar}>
          <input
            type="text"
            className={ui.toolbarSearch}
            placeholder="Search projects…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Urgencies' },
              { value: 'Low', label: 'Low' },
              { value: 'Medium', label: 'Medium' },
              { value: 'High', label: 'High' },
            ]}
            value={filterUrgency}
            onChange={(val) => setFilterUrgency(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Statuses' },
              ...statuses
                .filter((s) => showArchive
                  ? s.name?.toLowerCase() === "completed"
                  : s.name?.toLowerCase() !== "completed"
                )
                .map((s) => ({ value: String(s.status_id), label: s.name }))
            ]}
            value={filterStatus}
            onChange={(val) => setFilterStatus(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Labels' },
              ...availableLabels.map(([id, name]) => ({ value: String(id), label: name }))
            ]}
            value={filterLabel}
            onChange={(val) => setFilterLabel(val)}
            toolbar
          />
          <CustomDropdown
            options={[
              { value: '', label: 'All Employees' },
              ...availableEmployees.map((emp) => ({ value: String(emp.id), label: emp.name }))
            ]}
            value={filterEmployee}
            onChange={(val) => setFilterEmployee(val)}
            toolbar
          />
          {viewMode === 'table' && (
            <CustomDropdown
              options={[
                { value: '', label: 'No Grouping' },
                { value: 'status', label: 'Group by Status' },
                { value: 'urgency', label: 'Group by Urgency' },
                { value: 'client_company', label: 'Group by Company' },
              ]}
              value={groupBy}
              onChange={(val) => setGroupBy(val)}
              toolbar
            />
          )}
          {viewMode === 'table' && (
            <SegmentedControl
              ariaLabel="Project view"
              options={[{ value: 'active', label: 'Active' }, { value: 'completed', label: 'Completed' }]}
              value={showArchive ? 'completed' : 'active'}
              onChange={(v) => { setShowArchive(v === 'completed'); setFilterStatus(""); }}
            />
          )}
          <button
            className={ui.toolbarReset}
            onClick={resetView}
            title="Reset to default view"
          >
            Clear
          </button>
        </div>
      )}

      {/* New Project Form */}
      {showForm && canCreateProjects && (
        <FormModal
          title="New Project"
          milestones={milestones}
          error={error}
          onAddMilestoneClick={() => setShowMilestoneForm(true)}
          onCancel={() => {
            setShowForm(false);
            clearForm();
          }}
          onSubmit={handleSubmit}
          tabs={[
            { key: "overview", label: "Overview" },
            { key: "budget", label: "Budget" },
            canManageProjectTeam && { key: "team", label: "Team" },
            { key: "milestones", label: "Milestones" }
          ].filter(Boolean)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          assignedEmployees={assignedEmployees}
          setAssignedEmployees={setAssignedEmployees}
          availableEmployees={availableEmployees}
          employeeSearch={employeeSearch}
          setEmployeeSearch={setEmployeeSearch}
          overviewReadOnly={false}      // <-- Add this
          teamReadOnly={false}          // <-- Add this 
          fields={activeTab === "overview" ? overviewFields : activeTab === "budget" ? budgetFields : []}
        />
      )}

      {/* Project Details Modal (Read-only view) */}
      {showDetailModal && editableProject && (
        <DetailsModal
          title="Project Details"
          onClose={() => {
            setShowDetailModal(false);
            setEditableProject(null);
            setActiveTab("overview");
          }}
          accentColor={editableProject.color || '#94a3b8'}
          menuActions={canDeleteProjects ? [{
            label: "Delete project",
            danger: true,
            onClick: async () => {
              const confirmDelete = window.confirm("Are you sure you want to delete this project?");
              if (confirmDelete) {
                try {
                  await deleteProject(editableProject.project_id);
                  setProjects(prev => prev.filter(p => p.project_id !== editableProject.project_id));
                  setShowDetailModal(false);
                  setEditableProject(null);
                  showToast("Project deleted successfully", "success");
                } catch (err) {
                  console.error("Failed to delete project", err);
                  showToast(err.message || "Failed to delete project", "error");
                }
              }
            },
          }] : []}
          titleBar={
            <>
              <h3 className={detailStyles.recordTitle}>
                <EditableField
                  bare
                  value={editableProject.project_name}
                  disabled={!canEditProjects}
                  placeholder="Untitled project"
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, { project_name: val });
                    setEditableProject(prev => ({ ...prev, project_name: val }));
                    applyOptimisticProjectUpdate(editableProject.project_id, { project_name: val });
                  }}
                />
              </h3>
              <div className={detailStyles.recordMeta}>
                <span className={detailStyles.recordMetaClient}>
                  <Icon name="building" size="0.85rem" className={detailStyles.recordMetaIcon} />
                  {editableProject.client_company || editableProject.client_name || 'No client'}
                </span>
                <span className={detailStyles.metaSeparator}>·</span>
                <EditableField
                  bare
                  inline
                  type="select"
                  value={String(editableProject.status_id || '')}
                  options={statuses.map(s => ({ value: String(s.status_id), label: s.name, color: s.color }))}
                  disabled={!canEditProjects}
                  placeholder="No status"
                  formatDisplay={() => renderStatusPill(editableProject.status_id)}
                  onSave={handleInlineProjectStatusChange}
                />
              </div>
            </>
          }
          tabs={[
            { key: "overview", label: "Overview" },
            canViewProjectBudgets && { key: "budget", label: "Budget" },
            { key: "team", label: "Team" },
            { key: "milestones", label: "Milestones" },
            { key: "files", label: "Files" }
          ].filter(Boolean)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          sidePanel={
            <ActivityPanel
              entityType="project"
              entityId={editableProject.project_id}
              canComment={canViewAllProjects || canViewAssignedProjects}
              refreshSignal={activityRefreshKey}
              onFileUploaded={async () => {
                try {
                  const files = await activityService.listFiles('project', editableProject.project_id);
                  setProjectFiles(files || []);
                } catch (err) {
                  console.error("Failed to refresh project files:", err);
                }
              }}
            />
          }
        >
          {activeTab === "overview" && (
            <div>
              <div className={detailStyles.group}>
                <h4 className={detailStyles.groupTitle}>
                  <Icon name="status" size="0.9rem" className={detailStyles.groupTitleIcon} />
                  Properties
                </h4>

                <EditableField
                  label="Status"
                  icon="status"
                  type="select"
                  value={String(editableProject.status_id || '')}
                  options={statuses.map(s => ({ value: String(s.status_id), label: s.name, color: s.color }))}
                  disabled={!canEditProjects}
                  placeholder="No status"
                  formatDisplay={() => renderStatusPill(editableProject.status_id)}
                  onSave={handleInlineProjectStatusChange}
                />

                <EditableField
                  label="Priority"
                  icon="flag"
                  type="select"
                  value={editableProject.urgency || 'Low'}
                  options={[
                    { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'High', label: 'High' },
                  ]}
                  disabled={!canEditProjects}
                  formatDisplay={() => renderUrgencyPill(editableProject.urgency)}
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, { urgency: val });
                    setEditableProject(prev => ({ ...prev, urgency: val }));
                    applyOptimisticProjectUpdate(editableProject.project_id, { urgency: val });
                  }}
                />

                <div className={detailStyles.propRow}>
                  <span className={detailStyles.propLabel}>
                    <Icon name="calendar" size="0.95rem" className={detailStyles.propIcon} />
                    Timeline
                  </span>
                  <div className={detailStyles.propValue}>
                    <EditableField
                      bare
                      inline
                      type="date"
                      value={editableProject.start_date?.slice(0, 10) || ''}
                      disabled={!canEditProjects}
                      placeholder="Set start date"
                      formatDisplay={(val) => val ? formatDetailDate(val) : null}
                      onSave={async (val) => {
                        await updateProjectAndRefresh(editableProject.project_id, { start_date: val });
                        setEditableProject(prev => ({ ...prev, start_date: val }));
                      }}
                    />
                    <span className={detailStyles.timelineSeparator}>
                      <Icon name="arrowRight" size="0.85rem" />
                    </span>
                    <EditableField
                      bare
                      inline
                      type="date"
                      value={editableProject.end_date?.slice(0, 10) || ''}
                      disabled={!canEditProjects}
                      placeholder="Set end date"
                      formatDisplay={(val) => val ? formatDetailDate(val) : null}
                      onSave={async (val) => {
                        await updateProjectAndRefresh(editableProject.project_id, { end_date: val });
                        setEditableProject(prev => ({ ...prev, end_date: val }));
                      }}
                    />
                  </div>
                </div>

                <div className={detailStyles.propRow}>
                  <span className={detailStyles.propLabel}>
                    <Icon name="tag" size="0.95rem" className={detailStyles.propIcon} />
                    Labels
                  </span>
                  <div className={detailStyles.propValue}>
                    {editingLabels ? (
                      <>
                        <LabelSelector
                          selectedLabels={editLabels}
                          onChange={async (labels) => {
                            setEditLabels(labels);
                            try {
                              await setProjectLabels(editableProject.project_id, labels.map(l => l.label_id));
                            } catch (err) {
                              showToast(err.message || "Failed to update labels", "error");
                            }
                          }}
                          placeholder="Add service type labels..."
                        />
                        <button
                          type="button"
                          className={ui.cancelButton}
                          onClick={() => setEditingLabels(false)}
                        >
                          Done
                        </button>
                      </>
                    ) : editLabels.length > 0 ? (
                      <div
                        className={detailStyles.labelsList}
                        onClick={() => canEditProjects && setEditingLabels(true)}
                        style={{ cursor: canEditProjects ? 'pointer' : 'default' }}
                      >
                        {editLabels.map(label => (
                          <span
                            key={label.label_id}
                            className={detailStyles.labelTag}
                            style={{ backgroundColor: label.color || '#6b7280' }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={detailStyles.addInlineButton}
                        disabled={!canEditProjects}
                        onClick={() => setEditingLabels(true)}
                      >
                        <Icon name="plus" size="0.8rem" /> Add label
                      </button>
                    )}
                  </div>
                </div>

                <EditableField
                  label="Location"
                  icon="mapPin"
                  value={editableProject.location}
                  disabled={!canEditProjects}
                  placeholder="Add a location"
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, { location: val });
                    setEditableProject(prev => ({ ...prev, location: val }));
                  }}
                />

                <EditableField
                  label="Address"
                  icon="building"
                  type="textarea"
                  value={editableProject.address}
                  disabled={!canEditProjects}
                  placeholder="Add an address"
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, { address: val });
                    setEditableProject(prev => ({ ...prev, address: val }));
                  }}
                />

                <CustomFieldRows
                  fields={customFields}
                  entity={editableProject}
                  disabled={!canEditProjects}
                  formatMoney={formatMoney}
                  onSave={async (field, val) => {
                    await updateProjectAndRefresh(editableProject.project_id, {
                      custom_fields: { [field.field_id]: val },
                    });
                    const merged = { ...(editableProject.custom_fields || {}), [field.field_id]: val };
                    setEditableProject(prev => ({ ...prev, custom_fields: merged }));
                    // Custom fields are table columns too, so patch the row
                    // behind the modal rather than waiting for a refetch.
                    applyOptimisticProjectUpdate(editableProject.project_id, { custom_fields: merged });
                  }}
                />
              </div>

              <div className={detailStyles.group}>
                <h4 className={detailStyles.groupTitle}>
                  <Icon name="text" size="0.9rem" className={detailStyles.groupTitleIcon} />
                  Description
                </h4>
                <EditableField
                  bare
                  flush
                  type="textarea"
                  value={editableProject.description}
                  disabled={!canEditProjects}
                  placeholder="Add a description…"
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, { description: val });
                    setEditableProject(prev => ({ ...prev, description: val }));
                  }}
                />
              </div>

              <div className={detailStyles.group}>
                <h4 className={detailStyles.groupTitle}>
                  <Icon name="user" size="0.9rem" className={detailStyles.groupTitleIcon} />
                  Client
                </h4>

                <EditableField
                  label="Contact name"
                  icon="user"
                  value={editableProject.client_name}
                  disabled={!canEditProjects}
                  placeholder="Add a contact"
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, {
                      client_name: val,
                      client_company: editableProject.client_company,
                      email: editableProject.email,
                      phone_number: editableProject.phone_number,
                    });
                    setEditableProject(prev => ({ ...prev, client_name: val }));
                  }}
                />

                <EditableField
                  label="Company"
                  icon="building"
                  value={editableProject.client_company}
                  disabled={!canEditProjects}
                  placeholder="Add a company"
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, {
                      client_name: editableProject.client_name,
                      client_company: val,
                      email: editableProject.email,
                      phone_number: editableProject.phone_number,
                    });
                    setEditableProject(prev => ({ ...prev, client_company: val }));
                  }}
                />

                <EditableField
                  label="Email"
                  icon="mail"
                  value={editableProject.email}
                  disabled={!canEditProjects}
                  placeholder="Add an email"
                  formatDisplay={(val) => val ? (
                    <a
                      href={`mailto:${val}`}
                      className={detailStyles.inlineLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {val}
                    </a>
                  ) : null}
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, {
                      client_name: editableProject.client_name,
                      client_company: editableProject.client_company,
                      email: val,
                      phone_number: editableProject.phone_number,
                    });
                    setEditableProject(prev => ({ ...prev, email: val }));
                  }}
                />

                <EditableField
                  label="Phone"
                  icon="phone"
                  value={editableProject.phone_number}
                  disabled={!canEditProjects}
                  placeholder="Add a phone number"
                  onSave={async (val) => {
                    await updateProjectAndRefresh(editableProject.project_id, {
                      client_name: editableProject.client_name,
                      client_company: editableProject.client_company,
                      email: editableProject.email,
                      phone_number: val,
                    });
                    setEditableProject(prev => ({ ...prev, phone_number: val }));
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === "team" && (
            <div className={detailStyles.group}>
              <h4 className={detailStyles.groupTitle}>
                <Icon name="user" size="0.9rem" className={detailStyles.groupTitleIcon} />
                Team members
                {assignedEmployees.length > 0 && (
                  <span className={detailStyles.groupCount}>{assignedEmployees.length}</span>
                )}
              </h4>

              {canManageProjectTeam && (
                <div className={detailStyles.inlineSearch}>
                  <input
                    type="text"
                    className={detailStyles.searchInput}
                    placeholder="Search people by name or job title…"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    onFocus={() => setTeamSearchFocused(true)}
                    onBlur={() => setTimeout(() => setTeamSearchFocused(false), 200)}
                  />
                  {teamSearchFocused && (
                    <div className={detailStyles.searchResults}>
                      {availableEmployees
                        .filter(emp => (emp.name + emp.job_title).toLowerCase().includes(employeeSearch.toLowerCase()))
                        .filter(emp => !assignedEmployees.some(a => a.id === emp.id))
                        .map(emp => (
                          <button
                            key={emp.id}
                            type="button"
                            className={detailStyles.searchResult}
                            onClick={() => handleAddTeamMember(emp)}
                          >
                            {emp.name}
                            <span className={detailStyles.searchResultMeta}> — {emp.job_title}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {assignedEmployees.length > 0 ? (
                <div>
                  {assignedEmployees.map(emp => (
                    <div key={emp.id} className={detailStyles.listItem}>
                      <Avatar name={emp.name} imageUrl={emp.profile_picture} size="sm" />
                      <div className={detailStyles.listItemBody}>
                        <div className={detailStyles.listItemTitle}>{emp.name}</div>
                        {canManageProjectTeam ? (
                          <input
                            type="text"
                            className={detailStyles.roleInput}
                            placeholder="Role in project"
                            value={emp.role || ""}
                            onChange={(e) => setAssignedEmployees(prev => prev.map(a => a.id === emp.id ? { ...a, role: e.target.value } : a))}
                            onBlur={() => persistTeamAssignments(assignedEmployees)}
                          />
                        ) : (
                          <div className={detailStyles.listItemMeta}>
                            {emp.role || emp.job_title || 'Team member'}
                          </div>
                        )}
                      </div>
                      {canManageProjectTeam && (
                        <button
                          type="button"
                          className={detailStyles.iconButton}
                          onClick={() => handleRemoveTeamMember(emp.id)}
                          aria-label={`Remove ${emp.name}`}
                          title="Remove from project"
                        >
                          <Icon name="close" size="0.9rem" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={detailStyles.emptyMessage}>No team members assigned yet.</p>
              )}
            </div>
          )}

          {activeTab === "milestones" && (
            <div className={detailStyles.group}>
              <h4 className={detailStyles.groupTitle}>
                <Icon name="flag" size="0.9rem" className={detailStyles.groupTitleIcon} />
                Milestones
                {milestones.length > 0 && (
                  <span className={detailStyles.groupCount}>{milestones.length}</span>
                )}
                {canEditProjects && (
                  <button
                    type="button"
                    className={detailStyles.groupAction}
                    onClick={() => setShowMilestoneForm(true)}
                  >
                    <Icon name="plus" size="0.8rem" />
                    Add milestone
                  </button>
                )}
              </h4>

              {milestones.length > 0 ? (
                <div>
                  {milestones.map(milestone => (
                    <React.Fragment key={milestone.milestone_id}>
                    <div className={detailStyles.listItem}>
                      {/* Edit target is its own button so the delete control
                          isn't nested inside another button */}
                      <button
                        type="button"
                        className={detailStyles.listItemMain}
                        onClick={() => handleEditMilestone(milestone)}
                        title="Edit milestone"
                      >
                        <span className={detailStyles.listItemBody}>
                          <span className={detailStyles.listItemTitle}>{milestone.milestone_name}</span>
                          {milestone.due_date && (
                            <span className={detailStyles.listItemMeta}>
                              Due {formatDetailDate(milestone.due_date.slice(0, 10))}
                            </span>
                          )}
                        </span>
                        {renderMilestonePill(milestone.status)}
                      </button>
                      {(canEditProjects || milestone.jobs?.length > 0) && (
                        <button
                          type="button"
                          className={detailStyles.groupAction}
                          aria-expanded={openJobsFor === milestone.milestone_id}
                          onClick={() => setOpenJobsFor((cur) => (cur === milestone.milestone_id ? null : milestone.milestone_id))}
                        >
                          Jobs{milestone.jobs?.length > 0 ? ` ${milestone.jobs.filter((j) => j.done_at).length}/${milestone.jobs.length}` : ' +'}
                        </button>
                      )}
                      {canEditProjects && (
                        <button
                          type="button"
                          className={detailStyles.iconButton}
                          onClick={() => handleDeleteMilestone(milestone)}
                          aria-label={`Delete ${milestone.milestone_name}`}
                          title="Delete milestone"
                        >
                          <Icon name="trash" size="0.9rem" />
                        </button>
                      )}
                    </div>
                    {openJobsFor === milestone.milestone_id && (
                      <JobListEditor
                        jobs={milestone.jobs || []}
                        canEdit={canEditProjects}
                        canTick={canTickJobs(editableProject)}
                        onAdd={(title) => handleAddJob(milestone, title)}
                        onToggle={(job) => handleToggleJob(job, milestone)}
                        onRename={(job, title) => handleRenameJob(milestone, job, title)}
                        onRemove={(job) => handleRemoveJob(milestone, job)}
                      />
                    )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <p className={detailStyles.emptyMessage}>No milestones yet.</p>
              )}
            </div>
          )}

          {activeTab === "budget" && canViewProjectBudgets && (
            <div className={detailStyles.group}>
              <h4 className={detailStyles.groupTitle}>
                <Icon name="wallet" size="0.9rem" className={detailStyles.groupTitleIcon} />
                Budget
              </h4>

              <div className={detailStyles.statGrid}>
                <div className={detailStyles.statCard}>
                  <div className={detailStyles.statLabel}>
                    <Icon name="wallet" size="0.85rem" />
                    Estimated budget
                  </div>
                  <div className={detailStyles.statValue}>
                    <EditableField
                      bare
                      type="number"
                      value={budgetCost}
                      disabled={!canManageProjectBudgets}
                      placeholder="Set a budget"
                      formatDisplay={(val) => val ? formatMoney(val, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : null}
                      onSave={async (val) => {
                        const cost = val ? parseFloat(val) : 0;
                        await updateProjectBudgetAndRefresh(editableProject.project_id, {
                          budget_cost: cost,
                          budget_hours: budgetHours ? parseFloat(budgetHours) : 0,
                          notes: budgetNotes,
                        });
                        setBudgetCost(val);
                      }}
                    />
                  </div>
                </div>
                <div className={detailStyles.statCard}>
                  <div className={detailStyles.statLabel}>
                    <Icon name="clock" size="0.85rem" />
                    Estimated hours
                  </div>
                  <div className={detailStyles.statValue}>
                    <EditableField
                      bare
                      type="number"
                      value={budgetHours}
                      disabled={!canManageProjectBudgets}
                      placeholder="Set hours"
                      formatDisplay={(val) => val ? `${val} hrs` : null}
                      onSave={async (val) => {
                        const hours = val ? parseFloat(val) : 0;
                        await updateProjectBudgetAndRefresh(editableProject.project_id, {
                          budget_cost: budgetCost ? parseFloat(budgetCost) : 0,
                          budget_hours: hours,
                          notes: budgetNotes,
                        });
                        setBudgetHours(val);
                      }}
                    />
                  </div>
                </div>
              </div>

              <EditableField
                label="Notes"
                icon="text"
                type="textarea"
                value={budgetNotes}
                disabled={!canManageProjectBudgets}
                placeholder="Add budget notes…"
                onSave={async (val) => {
                  await updateProjectBudgetAndRefresh(editableProject.project_id, {
                    budget_cost: budgetCost ? parseFloat(budgetCost) : 0,
                    budget_hours: budgetHours ? parseFloat(budgetHours) : 0,
                    notes: val,
                  });
                  setBudgetNotes(val);
                }}
              />
            </div>
          )}

          {activeTab === "files" && (
            <div className={detailStyles.group}>
              <h4 className={detailStyles.groupTitle}>
                <Icon name="file" size="0.9rem" className={detailStyles.groupTitleIcon} />
                Files
                {projectFiles.length > 0 && (
                  <span className={detailStyles.groupCount}>{projectFiles.length}</span>
                )}
                {canEditProjects && (
                  <button
                    type="button"
                    className={detailStyles.groupAction}
                    onClick={() => projectFileInputRef.current?.click()}
                    disabled={uploadingProjectFile || !FILE_UPLOADS_ENABLED}
                    title={FILE_UPLOADS_ENABLED ? undefined : UPLOADS_DISABLED_MESSAGE}
                  >
                    <Icon name="upload" size="0.8rem" />
                    {uploadingProjectFile ? "Uploading…" : "Upload file"}
                  </button>
                )}
              </h4>

              <input
                ref={projectFileInputRef}
                type="file"
                className={detailStyles.hiddenFileInput}
                onChange={handleProjectFileUpload}
              />

              {projectFiles.length > 0 ? (
                <div>
                  {projectFiles.map(file => {
                    const canDelete = file.uploaded_by_user_id === currentUser?.user_id || canEditProjects;
                    return (
                      <div key={file.file_id} className={detailStyles.listItem}>
                        <Icon name="file" size="1.1rem" className={detailStyles.fileIcon} />
                        <div className={detailStyles.listItemBody}>
                          <button
                            type="button"
                            onClick={() => activityService.openAttachment("project", editableProject.project_id, file.file_id)}
                            className={detailStyles.listItemTitle}
                          >
                            {file.original_name}
                          </button>
                          <div className={detailStyles.listItemMeta}>
                            {file.uploaded_by_name || 'Unknown'} · {new Date(file.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {canDelete && (
                          <button
                            type="button"
                            className={detailStyles.iconButton}
                            aria-label={`Delete ${file.original_name}`}
                            title="Delete file"
                            onClick={() => handleProjectFileDelete(file)}
                          >
                            <Icon name="trash" size="0.9rem" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={detailStyles.emptyMessage}>
                  {FILE_UPLOADS_ENABLED
                    ? "No files yet — upload one to get started."
                    : "No files yet. Uploads are disabled until cloud storage is set up."}
                </p>
              )}
            </div>
          )}
        </DetailsModal>
      )}

      {showMilestoneForm && (
        <div className={ui.modalOverlay} style={{ zIndex: 1000001 }}>
          <div className={ui.modal}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
                Add Milestone
              </h2>
              <button
                type="button"
                onClick={() => setShowMilestoneForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className={ui.modalContent}>
              <div className={ui.inputGroup}>
                <label>Milestone Name</label>
                <input
                  type="text"
                  value={milestoneFormData.milestone_name}
                  onChange={(e) => setMilestoneFormData({ ...milestoneFormData, milestone_name: e.target.value })}
                />
              </div>

              <div className={ui.inputGroup}>
                <label>Milestone Dates</label>
                <div className={ui.inputRow}>
                  <div className={ui.inputGroup}>
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={milestoneFormData.start_date}
                      onChange={(e) => setMilestoneFormData({ ...milestoneFormData, start_date: e.target.value })}
                    />
                  </div>
                  <div className={ui.inputGroup}>
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={milestoneFormData.due_date}
                      onChange={(e) => setMilestoneFormData({ ...milestoneFormData, due_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className={ui.inputGroup}>
                <label>Status</label>
                <CustomDropdown
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'in progress', label: 'In Progress' },
                    { value: 'completed', label: 'Completed' },
                  ]}
                  value={milestoneFormData.status}
                  onChange={(val) => setMilestoneFormData({ ...milestoneFormData, status: val })}
                  fullWidth
                />
              </div>

              <div className={ui.inputGroup}>
                <label>Description</label>
                <textarea
                  value={milestoneFormData.description}
                  onChange={(e) => setMilestoneFormData({ ...milestoneFormData, description: e.target.value })}
                />
              </div>
            </div>

            <div className={ui.actions}>
              <button
                type="button"
                className={ui.cancelButton}
                onClick={() => setShowMilestoneForm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={ui.saveButton}
                onClick={async () => {
                  if (!editableProject?.project_id) {
                    showToast("Please save the project first before adding milestones", "error");
                    return;
                  }

                  if (!milestoneFormData.milestone_name?.trim()) {
                    showToast("Milestone name is required", "error");
                    return;
                  }

                  // Validate dates: start date should be same or earlier than due date
                  if (milestoneFormData.start_date && milestoneFormData.due_date) {
                    const startDate = new Date(milestoneFormData.start_date);
                    const dueDate = new Date(milestoneFormData.due_date);
                    if (startDate > dueDate) {
                      showToast("Start date cannot be later than due date", "error");
                      return;
                    }
                  }

                  try {
                    const newMilestone = await createMilestone(editableProject.project_id, {
                      ...milestoneFormData,
                    });

                    setMilestones(prev => [...prev, newMilestone]);

                    // Refresh projects list to show the new milestone
                    await refreshProjects();

                    // reset form
                    setMilestoneFormData({
                      milestone_name: "",
                      description: "",
                      start_date: "",
                      due_date: "",
                      status: "open",
                    });
                    setShowMilestoneForm(false);
                    showToast("Milestone created successfully", "success");
                  } catch (err) {
                    console.error("Failed to create milestone:", err);
                    showToast(err.message || "Failed to create milestone", "error");
                  }
                }}
              >
                Save Milestone
              </button>
            </div>
          </div>
        </div>
      )}

      {showMilestoneEditModal && editableMilestone && (
        <MilestoneEditModal
          milestone={editableMilestone}
          onMilestoneChange={setEditableMilestone}
          activeTab={activeMilestoneTab}
          onTabChange={setActiveMilestoneTab}
          employeeSearch={milestoneEmployeeSearch}
          onEmployeeSearchChange={setMilestoneEmployeeSearch}
          searchFocused={milestoneSearchFocused}
          onSearchFocusChange={setMilestoneSearchFocused}
          availableEmployees={availableEmployees}
          onClose={() => setShowMilestoneEditModal(false)}
          onSave={handleMilestoneAssignmentSave}
        />
      )}

      {showBatchExport && (
        <ExportDialog
          title="Export selected projects"
          filename="projects"
          scopes={[{ value: 'selected', label: 'Ticked projects', rows: displayedProjects.filter((p) => selectedIds.has(p.project_id)) }]}
          columns={projectExportColumns}
          groups={[
            { value: 'status', label: 'Status', get: statusName },
            { value: 'urgency', label: 'Urgency', get: (p) => p.urgency },
            { value: 'client_company', label: 'Company', get: (p) => p.client_company },
          ]}
          defaultGroup={groupBy}
          onExported={(count, kind) => showToast(`${count} ${count === 1 ? 'project' : 'projects'} exported${kind === 'csv' ? ' as CSV' : ''}.`, 'success')}
          onClose={() => setShowBatchExport(false)}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <FormModal
          title="Export Projects"
          submitLabel="Export"
          onCancel={() => setShowExportModal(false)}
          onSubmit={handleExportSubmit}
          fields={[
            {
              label: "Date Range (optional)",
              type: "custom",
              render: () => (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.startDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, startDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                  <div style={{ 
                    color: '#94a3b8', 
                    fontSize: '1.2rem',
                    paddingBottom: '6px'
                  }}>→</div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.endDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                </div>
              ),
            },
            {
              label: "Status (optional)",
              type: "select",
              value: exportForm.statusId,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, statusId: e.target.value })),
              options: [
                { value: "", label: "All statuses" },
                ...(statuses || []).map((s) => ({
                  value: s.status_id,
                  label: s.name
                }))
              ],
            },
            {
              label: "Urgency (optional)",
              type: "select",
              value: exportForm.urgency,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, urgency: e.target.value })),
              options: [
                { value: "", label: "All urgencies" },
                { value: "Low", label: "Low" },
                { value: "Medium", label: "Medium" },
                { value: "High", label: "High" }
              ],
            },
            {
              label: "Group By (optional)",
              type: "select",
              value: exportForm.groupBy,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, groupBy: e.target.value })),
              options: [
                { value: "", label: "No grouping" },
                { value: "company", label: "Client Company" },
                { value: "contact", label: "Client Name" }
              ],
            },
            ...sortAndFormatFields(exportForm, setExportForm, [
              { value: "project", label: "Project" },
              { value: "company", label: "Company" },
              { value: "status", label: "Status" },
              { value: "urgency", label: "Urgency" },
              { value: "start", label: "Start date" },
              { value: "end", label: "End date" },
            ]),
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeMilestones}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeMilestones: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include milestones in export</span>
                </label>
              ),
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeAssignments}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeAssignments: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include team members in export</span>
                </label>
              ),
            },
            ...(canViewProjectBudgets ? [{
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeBudget}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeBudget: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include budget information in export</span>
                </label>
              ),
            }] : []),
            ...(customFields.length > 0 ? [{
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeCustomFields}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeCustomFields: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include custom fields in export</span>
                </label>
              ),
            }] : []),
          ].filter(Boolean)}
        />
      )}

      {/* Table */}
      <div className={ui.dataList}>
        {/* <h2>{isViewingAssignedOnly ? 'My Projects' : 'Projects'}</h2> */}
        {showLoading ? (
          <div className={ui.tableSkeleton}>
            <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={`projects-skeleton-row-${idx}`} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />
            ))}
          </div>
        ) : displayedProjects.length === 0 ? (
          <div className={`${ui.emptyState} ${ui.fadeInContent}`}>
            <img
              src="\projects_empty.svg"
              alt="No projects"
              className={ui.emptyStateImage}
            />
            <h3>{showArchive ? (hasActiveFilters ? 'No matching archived projects' : 'No archived projects') : (isViewingAssignedOnly ? 'No assigned projects' : hasActiveFilters ? 'No matching projects' : 'No active projects')}</h3>
            <p>{showArchive ? (hasActiveFilters ? 'Try adjusting your search or filters' : 'Completed projects will appear here') : (isViewingAssignedOnly ? 'You have not been assigned to any projects yet' : hasActiveFilters ? 'Try adjusting your search or filters' : 'Create a new project to get started')}</p>
            {canCreateProjects && !hasActiveFilters && !showArchive && (
              <button 
                className={ui.addButton} 
                onClick={() => setShowForm(true)}
              >
                <img src={plusIcon} alt="" width="16" height="16" />
                Add Project
              </button>
            )}
          </div>
        ) : statuses.length > 0 && viewMode === 'kanban' ? (
          <div className={ui.fadeInContent} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <KanbanView
              columns={statuses.map((st) => ({ id: st.status_id, name: st.name, color: st.color }))}
              items={displayedProjects}
              getColumnId={(project) => project.status_id}
              canEdit={canEditProjects}
              onCardClick={openProjectDetail}
              getCardAccent={(project) => project.urgency === 'High' ? '#ef4444' : null}
              onCardMove={(project, newColumnId) => handleStatusChange(project, Number(newColumnId))}
              renderCard={(project) => {
                const urgencyColors = { Low: '#16a34a', Medium: '#d97706', High: '#dc2626' };
                const urgencyBgs = { Low: '#f0fdf4', Medium: '#fffbeb', High: '#fef2f2' };
                const done = (project.milestones || []).filter(isMilestoneDone).length;
                const total = (project.milestones || []).length;
                const pct = milestoneProgress(project);
                const late = (project.milestones || []).some(isMilestoneLate);
                const labels = project.labels || [];
                const team = project.assignees || [];
                return (
                  <>
                    <div className={kanban.cardTop}>
                      {project.urgency && (
                        <span className={kanban.cardBadge} style={{ background: urgencyBgs[project.urgency], color: urgencyColors[project.urgency] }}>
                          {project.urgency}
                        </span>
                      )}
                      <span className={kanban.cardDue}>
                        {project.end_date ? <>Due <DueDate value={project.end_date} done={statuses.find((st) => st.status_id === project.status_id)?.name === 'Completed'} /></> : null}
                      </span>
                    </div>
                    <p className={kanban.cardTitle}>{project.project_name || '—'}</p>
                    {project.client_company && <p className={kanban.cardSubtitle}>{project.client_company}</p>}
                    {labels.length > 0 && (
                      <div className={kanban.cardHeader}>
                        {labels.slice(0, 3).map((lb) => (
                          <span key={lb.label_id} className={kanban.cardLabelTag} title={lb.name}>
                            <span className={kanban.cardLabelDot} style={{ background: lb.color || '#a1a1aa' }} />
                            {lb.name}
                          </span>
                        ))}
                        {labels.length > 3 && <span className={kanban.cardLabelExtra}>+{labels.length - 3}</span>}
                      </div>
                    )}
                    <div className={kanban.cardProgress}>
                      <span>{done} of {total} milestones</span>
                      <strong style={late ? { color: '#b91c1c' } : undefined}>{pct}%</strong>
                    </div>
                    <div className={kanban.cardTrack}>
                      <div style={{ width: `${pct}%`, background: late ? '#dc2626' : pct === 100 ? '#22c55e' : '#3a6aed' }} />
                    </div>
                    {team.length > 0 && (
                      <div className={kanban.cardAvatars}>
                        {team.slice(0, 3).map((a) => (
                          <span key={a.employee_id} className={kanban.cardAvatar} title={a.user_name} style={{ background: avatarColorFromName(a.user_name) }}>
                            {getInitials(a.user_name)}
                          </span>
                        ))}
                        {(project.assignee_count || team.length) > 3 && (
                          <span className={`${kanban.cardAvatar} ${kanban.cardAvatarMore}`}>+{(project.assignee_count || team.length) - 3}</span>
                        )}
                      </div>
                    )}
                  </>
                );
              }}
            />
          </div>
        ) : statuses.length > 0 && viewMode === 'gantt' ? (
          <div className={ui.fadeInContent}>
            <GanttView
              projects={displayedProjects}
              canEditProjects={canEditProjects}
              onProjectClick={openProjectDetail}
              onMilestoneClick={handleEditMilestone}
              onProjectDateChange={async (projectId, dates) => {
                try {
                  await updateProject(projectId, dates);
                  await refreshProjects();
                  showToast("Project dates updated", "success");
                } catch (err) {
                  console.error("Failed to update project dates:", err);
                  showToast(err.message || "Failed to update project dates", "error");
                }
              }}
              onMilestoneDateChange={async (milestoneId, dates) => {
                try {
                  await updateMilestone(milestoneId, dates);
                  await refreshProjects();
                  showToast("Milestone dates updated", "success");
                } catch (err) {
                  console.error("Failed to update milestone dates:", err);
                  showToast(err.message || "Failed to update milestone dates", "error");
                }
              }}
            />
          </div>
        ) : statuses.length > 0 && (
          <div className={ui.fadeInContent}>
            <BatchBar count={selectedIds.size} onClear={clearSelection}>
              {canEditProjects && (
                <>
                  <CustomDropdown
                    options={[{ value: '', label: 'Change status…' }, ...statuses.map((st) => ({ value: String(st.status_id), label: st.name }))]}
                    value=""
                    onChange={(val) => val && batchStatus(Number(val))}
                    toolbar
                  />
                  <CustomDropdown
                    options={[{ value: '', label: 'Urgency…' }, ...['Low', 'Medium', 'High'].map((u) => ({ value: u, label: u }))]}
                    value=""
                    onChange={(val) => val && batchChange({ urgency: val })}
                    toolbar
                  />
                  <CustomDropdown
                    options={[{ value: '', label: 'Add to team…' }, ...availableEmployees.map((e) => ({ value: String(e.id), label: e.name }))]}
                    value=""
                    onChange={(val) => val && batchChange({ add_employee_id: Number(val) })}
                    toolbar
                  />
                  <CustomDropdown
                    options={[{ value: '', label: 'Add label…' }, ...availableLabels.map(([id, name]) => ({ value: String(id), label: name }))]}
                    value=""
                    onChange={(val) => val && batchChange({ add_label_id: Number(val) })}
                    toolbar
                  />
                </>
              )}
              <button type="button" onClick={() => setShowBatchExport(true)}>Export</button>
              {canDeleteProjects && <button type="button" onClick={() => setConfirmBatchDelete(true)}>Delete</button>}
            </BatchBar>
            <DataTable
              columns={columns}
              data={displayedProjects}
              selectable={canEditProjects || canDeleteProjects}
              selectedIds={selectedIds}
              onToggleRow={toggleSelectRow}
              onToggleGroup={toggleSelectGroup}
              onToggleAll={(checked) => toggleAll(displayedProjects.map((p) => p.project_id), checked)}
              storageKey={COLUMN_STORAGE_KEY}
              renderExpandedRow={(project) => (
                <MilestoneRows
                  milestones={project.milestones}
                  canEdit={canEditProjects}
                  canTickJobs={canTickJobs(project)}
                  onToggleMilestone={handleToggleMilestone}
                  onToggleJob={handleToggleJob}
                  onEdit={(m) => openProjectDetail(project).then(() => { setActiveTab("milestones"); if (m) setOpenJobsFor(m.milestone_id); })}
                />
              )}
              groupedBy={getGroupByValue()}
              collapsedGroups={collapsedGroups}
              groupOrder={groupBy === "status" ? statuses.filter(s => showArchive
                ? s.name?.toLowerCase() === 'completed'
                : s.name?.toLowerCase() !== 'completed'
              ).map(s => s.name) : []}
              toggleGroupCollapse={toggleGroupCollapse}
              onRowClick={openProjectDetail}
              onAddDetail={canManageCompanySettings ? () => setShowAddFieldModal(true) : null}
              onToggleColumn={toggleColumn}
              onMoveColumn={moveColumn}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              customCellRender={{
                status: (project) => showArchive ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{statuses.find(s => s.status_id === project.status_id)?.name || 'Completed'}</span>
                    {canEditProjects && (
                      <button
                        className={ui.restoreButton}
                        onClick={(e) => { e.stopPropagation(); handleRestoreProject(project); }}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ) : (
                  <StatusDropdown
                    statuses={statuses}
                    value={project.status_id}
                    onChange={(id) => handleStatusChange(project, id)}
                    onAddNew={() => { setAddStatusContext('table'); setShowAddStatusModal(true); }}
                    disabled={!canEditProjects}
                    compact
                    tinted
                  />
                ),
                urgency: (project) => (
                  <CustomDropdown
                    options={[
                      { value: 'Low', label: 'Low' },
                      { value: 'Medium', label: 'Medium' },
                      { value: 'High', label: 'High' },
                    ]}
                  value={project.urgency}
                  onChange={(val) => handleUrgencyChange(project, val)}
                  disabled={!canEditProjects}
                  compact
                />
              ),
              start_date: (project) =>
                project.start_date 
                  ? new Date(project.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : "—",
              end_date: (project) => <DueDate value={project.end_date} done={showArchive} />,
              project_name: (project) => (
                <div>
                  <div>{project.project_name || "—"}</div>
                  <MilestoneTag
                    milestones={project.milestones}
                    open={Boolean(expandedRows[project.project_id])}
                    onClick={() => toggleRowExpand(project.project_id)}
                  />
                </div>
              ),
              progress: (project) => {
                const pct = milestoneProgress(project);
                const late = project.milestones?.some(isMilestoneLate);
                return (
                  <div className={ui.progressCell}>
                    <span style={late ? { color: '#b91c1c' } : undefined}>{pct}%</span>
                    <div className={ui.progressTrack}><div className={`${ui.progressFill} ${late ? ui.progressFillLate : ''}`} style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              },              labels: (project) => {
                if (project.labels && project.labels.length > 0) {
                  return (
                    <div className={detailStyles.labelsList}>
                      {project.labels.map(label => (
                        <span 
                          key={label.label_id} 
                          className={detailStyles.labelTag}
                          style={{ backgroundColor: label.color || '#6b7280' }}
                        >
                          {label.name}
                        </span>
                      ))}
                    </div>
                  );
                }
                return "—";
              },
              ...customCellRenderers,
            }}
            expandedRows={expandedRows}
            toggleRowExpand={toggleRowExpand}
          />
          </div>
        )}
      </div>

      {confirmBatchDelete && (
        <ConfirmDialog
          message={`Delete ${selectedIds.size} project${selectedIds.size === 1 ? "" : "s"}? ${selectedIds.size === 1 ? "Its" : "Their"} milestones, bookings and files are removed too, and this can't be undone.`}
          confirmText="Delete"
          onConfirm={batchDelete}
          onCancel={() => setConfirmBatchDelete(false)}
        />
      )}

      {showArchiveConfirm && (
        <ConfirmDialog
          message="Are you sure this project is complete? It will be archived and removed from the active projects list."
          confirmText="Yes, Archive"
          cancelText="Cancel"
          onConfirm={async () => {
            setShowArchiveConfirm(false);
            if (pendingArchiveAction) {
              await pendingArchiveAction();
              setPendingArchiveAction(null);
            }
          }}
          onCancel={() => {
            setShowArchiveConfirm(false);
            setPendingArchiveAction(null);
          }}
        />
      )}

      {showAddStatusModal && (
        <AddStatusModal
          onSubmit={handleCreateStatus}
          onCancel={() => {
            setShowAddStatusModal(false);
            setAddStatusContext(null);
          }}
        />
      )}

      {showAddFieldModal && (
        <CustomFieldModal
          defaultEntity="project"
          onClose={() => setShowAddFieldModal(false)}
          onSaved={refreshCustomFields}
          onMessage={setToast}
        />
      )}

      <ReorderListModal
        isOpen={showStatusReorderModal}
        title="Reorder Project Statuses"
        description="Drag statuses to control their display order across project screens and filters."
        items={statuses}
        getId={(item) => item.status_id}
        getLabel={(item) => item.name}
        getColor={(item) => item.color}
        onClose={() => setShowStatusReorderModal(false)}
        onSave={handleSaveStatusOrder}
        saving={savingStatusOrder}
      />
    </div>
  );
}
